import { formatEtRange, pickEnum } from '@eo/shared';

import type {
  AdminCfoDay,
  ClientItem,
  CreateStaffBody,
  ListParams,
  StaffItem,
  UpdateClientBody,
  UpdateStaffBody,
} from './api/types';
import { ADMIN_TABS, REGISTRY_VIEWS, UNASSIGNED_OWNER } from './constants';
import {
  type AdminTab,
  type ClientOwnerChange,
  type ClientFormValues,
  type NewStaffValues,
  type RegistryView,
  type StaffFormValues,
  type TimeEntryRow,
} from './types';

// Reference lists (the owner picker on Clients) need every row, not the page
// the grid is showing — MAX_PER_PAGE on the service is 100.
export const REFERENCE_LIST_PARAMS: ListParams = { perPage: 100 };

// The registry grids write their state to the shared query string; only the
// view actually mounted should turn that into list params.
export function listParamsFor(
  view: RegistryView,
  activeTab: AdminTab,
  activeView: RegistryView,
  queryParams: Record<string, string | undefined>,
): ListParams {
  if (activeTab !== 'registries' || view !== activeView) return REFERENCE_LIST_PARAMS;

  const page = Number(queryParams.page);
  return {
    page: Number.isInteger(page) && page > 0 ? page : 1,
    sort: queryParams.sort || undefined,
    search: queryParams.search || undefined,
  };
}

export function parseTab(raw: string | null): AdminTab {
  return pickEnum(ADMIN_TABS, raw, 'overview');
}

export function parseRegistryView(raw: string | null): RegistryView {
  return pickEnum(REGISTRY_VIEWS, raw, 'staff');
}

export function parseYear(raw: string | null): number {
  const year = Number(raw);
  return Number.isInteger(year) && year >= 2000 && year <= 2100
    ? year
    : new Date().getUTCFullYear();
}

export function formatHours(value: number): string {
  return `${value.toFixed(2)}h`;
}

export const yesNo = (value: boolean): string => (value ? 'Yes' : '—');

export const orNull = (value: string): string | null => (value.trim() === '' ? null : value.trim());

export function formatChecked(iso: string | null): string {
  if (!iso) return '';
  const date = new Date(iso);
  return isNaN(date.getTime())
    ? ''
    : new Intl.DateTimeFormat('en-US', { month: 'short', day: 'numeric', year: 'numeric' }).format(
        date,
      );
}

// One row per entry across every member: the day is far easier to scan sorted
// by duration or searched by task than split across one table per CFO.
export function flattenTimeEntries(timeEntries: AdminCfoDay[]): TimeEntryRow[] {
  return timeEntries.flatMap((cfo) =>
    cfo.entries.map((entry) => ({
      id: `${cfo.userId}:${entry.id}`,
      cfo: cfo.name,
      time: formatEtRange(entry.startTs, entry.endTs),
      taskName: entry.taskName,
      client: entry.breadcrumb ?? '—',
      type: entry.billable ? 'Variable' : 'Fixed',
      durationMinutes: entry.durationMinutes,
    })),
  );
}

export function staffToValues(row: StaffItem, clients: ClientItem[]): StaffFormValues {
  return {
    name: row.name,
    isAdmin: row.isAdmin,
    isActive: row.isActive,
    role: row.role,
    country: row.country ?? '',
    clientIds: clients.filter((client) => client.ownerUserId === row.id).map((client) => client.id),
  };
}

// Ownership is a field on the client row, so the dialog's list turns into one
// PATCH per client that actually moved — never a blanket rewrite, which would
// clear an owner someone else set while this dialog was open.
export function clientOwnerChanges(
  before: string[],
  after: string[],
  userId: string,
): ClientOwnerChange[] {
  return [
    ...after
      .filter((id) => !before.includes(id))
      .map((clientId) => ({ clientId, ownerUserId: userId })),
    ...before
      .filter((id) => !after.includes(id))
      .map((clientId) => ({ clientId, ownerUserId: null })),
  ];
}

export function staffValuesToPatch(values: StaffFormValues): UpdateStaffBody {
  return {
    name: values.name.trim(),
    isAdmin: values.isAdmin,
    isActive: values.isActive,
    role: values.role,
    country: orNull(values.country),
  };
}

export function staffValuesToCreateBody(values: NewStaffValues): CreateStaffBody {
  return { email: values.email.trim(), name: values.name.trim() };
}

export function clientToValues(row: ClientItem): ClientFormValues {
  return {
    name: row.name,
    ownerUserId: row.ownerUserId ?? UNASSIGNED_OWNER,
    isActive: row.isActive,
    aliases: (row.aliases ?? []).join(', '),
    domains: (row.domains ?? []).join(', '),
  };
}

function listOf(text: string): string[] {
  return [
    ...new Set(
      text
        .split(',')
        .map((part) => part.trim())
        .filter(Boolean),
    ),
  ];
}

// A domain is matched against an address, which is case-insensitive; an alias
// keeps the case it was typed in, for whoever reads the list back.
export function clientMatchFields(values: ClientFormValues): {
  aliases: string[];
  domains: string[];
} {
  return {
    aliases: listOf(values.aliases),
    domains: listOf(values.domains.toLowerCase()),
  };
}

export function clientValuesToPatch(values: ClientFormValues): UpdateClientBody {
  return {
    name: values.name.trim(),
    ownerUserId: values.ownerUserId === UNASSIGNED_OWNER ? null : values.ownerUserId,
    isActive: values.isActive,
    ...clientMatchFields(values),
  };
}

// Temporary, until Mission Control is stable: the member's own day, read-only,
// so a reported bug can be reproduced without their login.
export function memberDayHref(email: string, date: string): string {
  return `/time-tracking?${new URLSearchParams({ date, as: email }).toString()}`;
}
